import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import type { WidgetConfig, WidgetSize } from '@/types/dashboards';

import WidgetCardShell from './WidgetCardShell';

const SIZE_COL_SPAN: Record<WidgetSize, string> = {
    small: 'xl:col-span-1',
    medium: 'xl:col-span-2',
    full: 'xl:col-span-4',
};

interface SortableWidgetCardProps {
    widget: WidgetConfig;
    editable: boolean;
    onEdit?: (widget: WidgetConfig) => void;
    onRemove?: (id: string) => void;
    /** The "(i)" explaining what this widget queried. Shown in view mode too, unlike edit/remove. */
    info?: ReactNode;
    children: ReactNode;
}

export default function SortableWidgetCard({ widget, editable, onEdit, onRemove, info, children }: SortableWidgetCardProps) {
    const { t } = useTranslations();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id, disabled: !editable });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const dragHandle = editable ? (
        <button
            {...attributes}
            {...listeners}
            className="text-muted-foreground hover:text-foreground cursor-grab touch-none transition-colors active:cursor-grabbing"
            aria-label={t('dashboards.builder.drag_handle', 'Drag to reorder')}
        >
            <GripVertical className="h-4 w-4" />
        </button>
    ) : null;

    // Info sits outside the `editable` guard: a read-only viewer of a shared
    // board is exactly who most needs to know where the number came from.
    const actions = (
        <>
            {info}
            {editable && (
                <>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => onEdit?.(widget)}
                        aria-label={t('dashboards.builder.edit_widget', 'Edit widget')}
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => onRemove?.(widget.id)}
                        aria-label={t('dashboards.builder.remove_widget', 'Remove widget')}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </>
            )}
        </>
    );

    return (
        <div ref={setNodeRef} style={style} className={cn('col-span-1', SIZE_COL_SPAN[widget.size])}>
            <WidgetCardShell title={widget.title} dragHandle={dragHandle} actions={actions}>
                {children}
            </WidgetCardShell>
        </div>
    );
}
